
import Fuse from 'fuse.js';

import SearchItem from './search-item';
import { getFOB } from '~/data-utils';
import Events from '~/events';

const OPTIONS = {
  includeScore: true,
  includeMatches: true,
  minMatchCharLength: 3,
  threshold: .4,
  location: 0,
  distance: 8,
  keys: [
    {
      name: 'primary',
      weight: 0.8
    },
    {
      name: 'secondary',
      weight: 0.7
    }
  ]
};

/** @type {[string, (x: any) => SearchItem][]} */
const TRANSFORMERS = [
  ['stores', storeToItem],
  ['offers', offerToItem],
  ['facilities', x => x],
  ['news', newsToItem],
];

// buildIndex :: IndexData -> Fuse
//  IndexData = { [type]: [ItemData] }
//  ItemData = Store|Offer
export default class SearchIndex {

  constructor (data) {

    const items = TRANSFORMERS.reduce((acc, [type, transform], idx) => {
      const itemData = data[type];
      if (!itemData) {
        console.warn(`Missing search data for '${type}'`);
        return acc;
      }
      try {
        return acc.concat(
          reduceLocals(transform, itemData)
        );
      } catch (e) {
        console.error(`Can't transfor data ${idx}:${type}`, e);
        console.log(data);
        return acc;
      }
    }, []);

    this.fuse = new Fuse(items, OPTIONS);
  }

  search (term) {
    const results = this.fuse.search(term)
      .filter(({ score }) => score < .2);
    
    Events.dispatchEvent('search', { term });
    return results;
  }

  autocomplete (term) {
    if (!term) {
      return { tips: [], result: [] };
    }

    const result = this.fuse.search(term);
    const set = new Set();

    const tips = result
      .filter(({ score }) => score < .08)
      .filter(x => x.matches.length)
      .reduce((acc, x) => {
        try {
          const tip = tipFromMatches(x);
          const notDublicate = !set.has(tip.toLowerCase());
          if (notDublicate) {
            set.add(tip.toLowerCase());
            acc.push({
              tip,
              score: x.score,
              refIndex: x.refIndex
            });
          }
        } catch (e) {
          console.log('Issue with match', e);
        }

        return acc;
      }, []);

    return {
      tips,
      result
    };
  }

  getIndex () {
    return this.fuse.getIndex();
  }

}

function tipFromMatches ({ matches }) {

  const [first] = matches;
  const { value, indices, key } = first;
  if (key == 'primary') return value;

  // indices might not match word boundaries
  const [[left, right]] = indices;

  const [, hit] = value.split(/(\s+)/).reduce((acc, part) => {
    const [fIndex] = acc,
          lIndex = fIndex + part.length;
    const isHitWord = left >= fIndex && right < lIndex;

    return [lIndex, isHitWord ? part : acc[1]];
  }, [0]);
  return hit;
}

// reduceLocals :: (Transform, [ItemData]) -> [SearchItem]
function reduceLocals (transform, data) {

  if (!data[0]) {
    throw new Error(typeof data[0]);
  }

  const map = data
    .reduce((acc, x) => acc.concat(x), []) // flatMap
    .reduce((acc, x) => {
      const item = transform(x);
      const current = acc.get(item.id);
      acc.set(item.id, current ?
        current.merge(item) :
        item
      );
      return acc;
    }, new Map());

  return Array.from(map.values());
}

// https://regex101.com/r/CuG2f4/1
const DELIM_REGEX_LARGE = /([\s.,;:!?+\\"'#€$%&/()=?´`^¨~*_-]|\\r\\n)+/gm;
const DELIM_REGEX = /((\s*[,;:|]+\s*)|\\r\\n)/g;
const TMP_DELIM = '||||';

// split :: (String, Regex) -> [String]
function split (value, regex, lenght_gt = 1) {
  return value
    .replace(regex, TMP_DELIM)          // Unify delimiters
    .split(TMP_DELIM)                   // Split by delims
    .map(x => x.trim())                 // Trim
    .filter(x => !!x && x.length > lenght_gt); // Remove empty strings
}

function asIsAndSplit (value, regex) {
  const parts = split(value, regex);
  return parts.length == 1 ? parts : [value].concat(parts);
}

function storeToItem (store) {
  const {
    id,
    fieldOfBusinessId,
    name,
    keywords = '',
    vendors = ''
  } = store;

  const fob = (getFOB(fieldOfBusinessId) || {}).description;

  return SearchItem.of({
    id,
    primary: [...asIsAndSplit(name, /\s/g), fob],
    secondary: [...split(keywords, DELIM_REGEX), ...split(vendors, DELIM_REGEX)],
    type: 'store'
  });
}

function offerToItem (offer) {
  const {
    id,
    title,
    // longDescription,
    store = { name: '' } // No store if the offer is from the shopping center
  } = offer;

  return SearchItem.of({
    id,
    primary: [title, store.name],
    // secondary: split(longDescription, DELIM_REGEX_LARGE, 3),
    secondary: [], // see https://jira.hyperin.com/browse/IMD-877
    type: 'offer'
  });
}

function newsToItem (newsItem) {
  const {
    id,
    title,
    keywords = []
  } = newsItem;

  return SearchItem.of({
    id,
    primary: split(title, DELIM_REGEX_LARGE, 3),
    secondary: keywords.map(x => x.trim()),
    type: 'newsAndEvent'
  });
}
